import { useAtomValue, useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { useCallback, useEffect } from "react";
import {
  exitPictureInPicture,
  isShowingScreenShareConfirmationAtom,
  remoteScreenShareParticipantAtom,
  roomAtom,
} from "../../video-sdk";
import {
  useStartScreenShare as useBaseStartScreenShare,
  useStopScreenShare as useBaseStopScreenShare,
} from "../../video-sdk/actions/useScreenShareActions";
import { shouldTriggerPictureInPictureAtom } from "../../video-sdk/twilio";

export type ShareScreenOptions = {
  shouldTakeOverScreenShare?: boolean;
};

/**
 * Session-level screen share hook.
 * Adds session-specific behaviors on top of core SDK actions:
 * - Picture-in-picture management
 * - Takeover confirmation modal
 * - Auto-stop when remote participant shares
 * - Convenience wrapper for stop-then-start
 */
export default function useScreenShareCallbacks(
  onError?: (error: Error) => void,
) {
  const baseStartScreenShare = useBaseStartScreenShare(onError);
  const baseStopScreenShare = useBaseStopScreenShare();

  const setShouldTriggerPictureInPicture = useSetAtom(
    shouldTriggerPictureInPictureAtom,
  );
  const screenShareParticipant = useAtomValue(remoteScreenShareParticipantAtom);
  const getIsSomeoneElseSharing = useAtomCallback(
    useCallback((get) => !!get(remoteScreenShareParticipantAtom), []),
  );
  const setIsShowingScreenShareConfirmation = useSetAtom(
    isShowingScreenShareConfirmationAtom,
  );
  const getRoom = useAtomCallback(useCallback((get) => get(roomAtom), []));

  /**
   * Start screen share with session-specific behaviors:
   * - Checks for takeover confirmation
   * - Triggers picture-in-picture
   * - Cleans up PiP on failure
   */
  const shareScreen = useCallback(
    async ({ shouldTakeOverScreenShare = true }: ShareScreenOptions = {}) => {
      // Check if someone else is sharing (session concern)
      if (!shouldTakeOverScreenShare && getIsSomeoneElseSharing()) {
        setIsShowingScreenShareConfirmation(true);
        return;
      }

      setIsShowingScreenShareConfirmation(false);

      /**
       * Trigger PiP immediately (session-specific UX concern)
       * Must happen before getDisplayMedia due to user gesture timeout
       */
      setShouldTriggerPictureInPicture(true);
      const success = await baseStartScreenShare();

      if (!success) {
        // Clean up PiP if screen share failed (session concern)
        exitPictureInPicture();
      }

      return success;
    },
    [
      getIsSomeoneElseSharing,
      setIsShowingScreenShareConfirmation,
      setShouldTriggerPictureInPicture,
      baseStartScreenShare,
    ],
  );

  /**
   * Stop screen share with session-specific cleanup
   */
  const stopScreenShare = useCallback(async () => {
    setIsShowingScreenShareConfirmation(false);
    await baseStopScreenShare();
  }, [baseStopScreenShare, setIsShowingScreenShareConfirmation]);

  /**
   * Convenience wrapper: stop current share then start new one
   * Useful for "share different screen" feature
   */
  const triggerBrowserScreenShare = useCallback(
    async (...args: Parameters<typeof shareScreen>) => {
      await stopScreenShare();
      const room = getRoom();
      if (room) {
        await shareScreen(...args);
      }
    },
    [shareScreen, stopScreenShare, getRoom],
  );

  /**
   * Auto-stop when remote participant starts sharing
   * (Session-specific coordination behavior)
   */
  useEffect(
    function stopScreenShareWhenOtherParticipantShares() {
      if (screenShareParticipant) {
        stopScreenShare();
      }
    },
    [screenShareParticipant, stopScreenShare],
  );

  return {
    triggerBrowserScreenShare,
    stopScreenShare,
  };
}
